/**
 * 答题页（会话）
 * ------------------------------------------------------------
 * 流程：读取 pending-session → 生成题目 → 逐题作答 → 结算 → 展示成绩单
 *
 * 这是学生直接使用的核心页面，要求：
 *   - 每题都能看到进度、可以发音、可以用提示
 *   - 判分立即反馈，并给出解析
 *   - 结算时写库（sessions/answers/errors/mastery）+ 发 XP + 判成就
 *   - 成绩永久保留，不覆盖
 */
import { getUnitContent } from '../services/dataSource.js';
import { createSessionFromGenerator, PracticeSession } from '../engines/practiceEngine.js';
import { priorityFor, mergeContents } from '../engines/sampler.js';
import { saveSession } from '../services/records.js';
import { settleSession } from '../services/rewards.js';
import { card, progressBar, confirmDialog, tag } from '../components/ui.js';
import { renderAnswerZone, bindAnswerZone, renderPrompt, bindSpeak, renderFeedback } from '../components/quiz.js';
import { navigate } from '../core/router.js';
import { escapeHtml, fmtDuration, autoSpeak, toast } from '../utils/index.js';
import { playWord, playSentenceEn } from '../services/audioService.js';

export default async function renderSession(mount) {
  const raw = sessionStorage.getItem('pending-session');
  let cfg = null;
  try { cfg = raw ? JSON.parse(raw) : null; } catch { cfg = null; }

  // 复习页直接给了题目数组的情况
  if (cfg?.presetQuestions?.length) {
    sessionStorage.removeItem('pending-session');
    return runPreset(mount, cfg);
  }

  if (!cfg || !cfg.generatorId) {
    mount.innerHTML = `<div class="empty"><span class="icon">🤔</span>
      <p><strong>没有待进行的练习</strong></p>
      <p class="muted">请先在「练习中心」选择一种练习方式。</p>
      <a class="btn btn-primary" href="#/practice">去练习中心</a></div>`;
    return;
  }
  sessionStorage.removeItem('pending-session');

  mount.innerHTML = '<div class="page-loading">正在准备题目…</div>';

  // 1) 取内容（支持跨单元复习：cfg.unitIds）
  let content;
  const unitId = cfg.unitId;
  if (cfg.unitIds?.length > 1) {
    const list = await Promise.all(cfg.unitIds.map(id => getUnitContent(cfg.textbookId, id)));
    content = mergeContents(list);
  } else {
    content = await getUnitContent(cfg.textbookId, unitId);
  }

  // 2) 优先级（错题 / 薄弱）
  const priority = cfg.strategy ? await priorityFor(unitId, cfg.strategy) : null;

  // 3) 生成题目
  let session;
  try {
    session = createSessionFromGenerator({ ...cfg, content, unitId, priority });
  } catch (err) {
    console.error(err);
    mount.innerHTML = `<div class="empty"><span class="icon">⚠️</span>
      <p><strong>题目生成失败</strong></p>
      <p class="muted">${escapeHtml(err.message)}</p>
      <a class="btn btn-primary" href="#/practice">返回练习中心</a></div>`;
    return;
  }

  if (!session.total) {
    const catTip = cfg.category
      ? `本单元在「${escapeHtml(cfg.category)}」这个知识点下暂时没有可用题目（教材里该知识点还没有录入原题，也没有可用的例句）。
         请换一个知识点，或选「混合练习」。`
      : `可能是本单元内容还没导入，或所选级别没有对应内容。`;
    mount.innerHTML = `<div class="empty"><span class="icon">📭</span>
      <p><strong>${cfg.category ? '这个知识点还没有题目' : '这个单元没有可出的题'}</strong></p>
      <p class="muted">${catTip}</p>
      <a class="btn btn-primary" href="#/practice?module=grammar">换一个知识点</a>
      <a class="btn btn-ghost" href="#/import">去导入题目</a></div>`;
    return;
  }

  runQuiz(mount, session, cfg);
}

/* --------------------------- 复习模式（题目已给定） --------------------------- */

function runPreset(mount, cfg) {
  const session = new PracticeSession({
    module: cfg.module || 'review',
    questions: cfg.presetQuestions,
    unitId: cfg.unitId,
    textbookId: cfg.textbookId,
    title: cfg.title || '错题复习',
  });
  runQuiz(mount, session, cfg);
}

/* ------------------------------ 答题主循环 ------------------------------ */

function runQuiz(mount, session, cfg) {
  let answered = false;

  function shell() {
    const q = session.current;
    mount.innerHTML = `
      <div class="quiz-wrap">
        <div class="quiz-top">
          <button class="icon-btn" id="q-quit" type="button" title="退出">✕</button>
          <div class="quiz-progress">
            <div class="row between" style="font-size:12px;margin-bottom:5px">
              <span class="muted">第 ${session.index + 1} / ${session.total} 题</span>
              ${tag(session.title || '练习', 'blue')}
            </div>
            ${progressBar(session.progress * 100)}
          </div>
          <button class="btn btn-sm btn-ghost" id="q-hint" type="button" ${q.hint ? '' : 'disabled'}>💡 提示</button>
        </div>

        <div id="q-prompt"></div>
        <div class="quiz-body" id="q-body"></div>
        <div id="q-feedback"></div>
      </div>`;

    const promptBox = mount.querySelector('#q-prompt');
    promptBox.innerHTML = renderPrompt(q, { showHint: false });
    bindSpeak(promptBox, q);

    mount.querySelector('#q-quit').onclick = async () => {
      const ok = await confirmDialog({
        title: '退出练习',
        message: '已答的题目会保留并计入成绩，未答的不会计入。确定退出吗？',
        okText: '退出',
        danger: true,
      });
      if (!ok) return;
      if (session.records.length) finish(mount, session, cfg, { aborted: true });
      else navigate('#/practice');
    };

    const hintBtn = mount.querySelector('#q-hint');
    hintBtn.onclick = () => {
      session.useHint();
      hintBtn.disabled = true;
      promptBox.innerHTML = renderPrompt(q, { showHint: true });
      bindSpeak(promptBox, q);
    };

    const body = mount.querySelector('#q-body');
    body.innerHTML = renderAnswerZone(q);
    bindAnswerZone(body, q, value => submit(value));
    answered = false;

    function submit(value) {
      if (answered || value === null || value === undefined) return;
      if (typeof value === 'string' && !value.trim()) return;
      if (Array.isArray(value) && !value.length) return;
      answered = true;

      const result = session.submit(value);
      const answeredQ = session.questions[session.index - 1];

      body.querySelectorAll('button, input').forEach(b => { b.disabled = true; });
      body.querySelectorAll('.opt').forEach(b => {
        if (b.dataset.value === (result.expected || '')) b.classList.add('correct');
        else if (!result.correct && b.dataset.value === value) b.classList.add('wrong');
      });

      const fb = mount.querySelector('#q-feedback');
      fb.innerHTML = renderFeedback(result, answeredQ)
        + `<div class="quiz-actions">
             <button class="btn btn-primary btn-lg" id="q-next" type="button">
               ${session.index >= session.total ? '查看成绩' : '下一题 →'}
             </button>
           </div>`;

      const nextBtn = mount.querySelector('#q-next');
      const onKey = e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          document.removeEventListener('keydown', onKey);
          nextBtn?.click();
        }
      };
      document.addEventListener('keydown', onKey);

      nextBtn.onclick = () => {
        document.removeEventListener('keydown', onKey);
        if (session.finished) finish(mount, session, cfg);
        else shell();
      };
    }
  }

  shell();
}

/* ------------------------------ 结算 ------------------------------ */

async function finish(mount, session, cfg, { aborted = false } = {}) {
  mount.innerHTML = '<div class="page-loading">正在结算…</div>';

  const summary = session.summary();
  let reward = { xp: 0, level: null, unlocked: [] };

  try {
    // 先落库再结算：成就判定依赖刚写入的这次练习记录，
    // 顺序反了会导致 total_sessions / unit_* 类成就永远差一次。
    await saveSession(summary);
    reward = await settleSession(summary);
  } catch (err) {
    console.error('[session] 结算失败', err);
    toast('成绩保存失败：' + err.message, 'error');
  }

  const wrongs = summary.records.filter(r => !r.correct);
  const lv = reward.level || { level: 1, xp: 0 };

  const skillRows = Object.entries(summary.bySkill)
    .map(([k, v]) => `<div class="row between" style="font-size:13px;margin-bottom:6px">
        <span>${skillName(k)}</span><b>${v.correct}/${v.total}</b></div>
      ${progressBar(v.total ? (v.correct / v.total) * 100 : 0, 'sm')}`)
    .join('') || '<p class="muted">—</p>';

  const levelRows = Object.entries(summary.byLevel)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([k, v]) => `<div class="row between" style="font-size:13px;margin-bottom:6px">
        <span>L${k}</span>
        <b class="${v.correct === v.total ? 'sym-ok' : v.correct ? 'sym-hint' : 'sym-no'}">${v.correct}/${v.total}</b></div>`)
    .join('') || '<p class="muted">—</p>';

  const unlockedHtml = (reward.unlocked || []).length
    ? card(`<div class="achievement-row">
        ${reward.unlocked.map(a => `<div class="achievement-pop">
            <span class="ico">${a.icon || '🏅'}</span>
            <div><strong>${escapeHtml(a.name)}</strong><small>${escapeHtml(a.desc || '')} · +${a.xp || 0} XP</small></div>
          </div>`).join('')}
      </div>`, { title: '🎉 新解锁成就' })
    : '';

  const wrongHtml = wrongs.length
    ? card(`<div class="wrong-list">
        ${wrongs.map(w => `<div class="wrong-item">
            <div class="grow">
              <div style="font-size:13px">${escapeHtml(String(w.prompt).replace(/\n/g, ' '))}</div>
              <div class="muted" style="font-size:12px">
                你的答案：<span class="sym-no">${escapeHtml(w.response || '（空）')}</span> ·
                正确答案：<span class="sym-ok">${escapeHtml(w.expected || '')}</span>
              </div>
            </div>
            <button class="icon-btn" data-en="${escapeHtml(w.expected || '')}" data-unit="${w.unitId || cfg?.unitIds?.[0] || ''}" data-ref-type="${w.refType || 'word'}" data-ref-id="${escapeHtml(w.refId || '')}" type="button">🔊</button>
          </div>`).join('')}
      </div>`, { title: `本次错题回顾（${wrongs.length}）` })
    : card('<p class="sym-ok" style="text-align:center;padding:12px">全部答对，太厉害了！🎉</p>', { title: '本次错题回顾' });

  mount.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero ${summary.score >= 80 ? 'good' : summary.score >= 60 ? 'mid' : 'low'}">
        <div class="result-score">${summary.score}</div>
        <div class="result-sub">${aborted ? '（提前结束）' : ''}${summary.total} 题中答对 ${summary.correct} 题 · 用时 ${fmtDuration(summary.durationMs)}</div>
        <div class="result-tags">
          <span class="tag green">✓ 独立 ${summary.independent}</span>
          <span class="tag orange">△ 提示后 ${summary.hinted}</span>
          <span class="tag red">× 未完成 ${summary.failed}</span>
        </div>
      </div>

      <div class="grid c3" style="margin-top:16px">
        ${card(`<div class="xp-gain">+${reward.xp} <small>XP</small></div>
          <div class="muted" style="font-size:12.5px">当前 Lv.${lv.level} · 距离下一级 ${100 - (lv.xpInLevel ?? 0)} XP</div>
          ${progressBar((lv.progress ?? 0) * 100)}`, { title: '本轮经验' })}
        ${card(skillRows, { title: '分技能表现' })}
        ${card(levelRows, { title: '分级表现（哪一级出错）' })}
      </div>

      ${unlockedHtml}
      ${wrongHtml}

      <div class="row mt-8" style="gap:10px;justify-content:center;flex-wrap:wrap">
        <button class="btn btn-primary" id="again" type="button">再练一次</button>
        <button class="btn btn-ghost" id="to-review" type="button">去复习错题</button>
        <a class="btn btn-ghost" href="#/report">查看学习数据</a>
        <a class="btn btn-ghost" href="#/home">回首页</a>
      </div>
    </div>`;

  mount.querySelectorAll('[data-speak]').forEach(b => {
    b.addEventListener('click', () => autoSpeak(b.dataset.speak));
  });
  // 新版：优先查 content 里的 MP3，查不到才走 TTS fallback
  mount.querySelectorAll('[data-en]').forEach(b => {
    b.addEventListener('click', async () => {
      const en = b.dataset.en;
      const unitId = b.dataset.unit;
      const refType = b.dataset.refType;
      const refId = b.dataset.refId;
      let audio = '';
      if (unitId && refType === 'word') {
        try {
          const c = await getUnitContent(cfg?.textbookId || '', unitId);
          const w = (c.words || []).find(w => String(w.id) === refId || w.en === en);
          audio = w?.audio || '';
        } catch { /* fallback */ }
      }
      if (audio) playWord({ en, audio });
      else if (refType === 'sentence') playSentenceEn(en);
      else playWord({ en });
    });
  });
  mount.querySelector('#again')?.addEventListener('click', () => {
    sessionStorage.setItem('pending-session', JSON.stringify({ ...cfg, at: Date.now() }));
    navigate('#/session');
  });
  mount.querySelector('#to-review')?.addEventListener('click', () => navigate('#/review'));
}

function skillName(k) {
  return { W: '单词', S: '句子', G: '语法', R: '记忆提取' }[k] || k;
}
